<template>

    <div ref="lssTable"
         class="lss-table"
         :class="{
            'is-mobile': isMobile,
         }"
         v-hotkey="keymap"
    >

        <BLoading v-if="loading" active/>

        <div class="columns table-header">

            <div class="column">

                <div class="is-flex is-align-items-center">
                    <Title v-if="title" style="margin: 2px 0;">
                        <BIcon :icon="titleIcon" v-if="titleIcon" custom-class="fa-fw" style="margin-left: 5px;" />
                        {{ title }}
                    </Title>
                    <BDropdown class="has-margin-left" v-if="showManageDropdown">
                        <template #trigger="{ dropdownActive }">
                            <BButton class="is-primary">
                                Manage
                                <BIcon icon="caret-down" style="margin-left: 0; position: relative; top: 2px;"/>
                            </BButton>
                        </template>
                        <BDropdownItem v-for="(action, i) in bulkActions" :key="`bulk-action-${i}`" v-if="action.alwaysShow || selected.length" @click="handleBulkAction(action)">
                            {{ action.label }}
                        </BDropdownItem>
                    </BDropdown>
                </div>

            </div>

            <div class="column">

                <div class="table-controls">

                    <div class="search">
                        <div class="search-icon">
                            <BIcon icon="search"/>
                        </div>
                        <TextInput @keyup.esc.native="handleCancelSearch"
                                   @keyup.enter.native="handleSearch"
                                   placeholder="Search"
                                   ref="search"
                                   v-if="action"
                                   v-model="search"
                        />
                        <span @click="handleCancelSearch" class="search-cancel pointer" v-if="search.length">
                        <BIcon icon="times"/>
                    </span>
                    </div>

                    <router-link class="button is-primary has-margin-left" :to="{ name: addRoute, query: addRouteQuery }"
                                 v-if="addRoute && addLabel">
                        {{ addLabel }}
                    </router-link>

                    <div class="column-control" v-if="isMobile">
                      <BDropdown :close-on-click="false" aria-role="list" position="is-bottom-left" class="has-text-left pointer" v-if="this.showColumns" @click.native.stop :mobile-modal="false">
                        <template #trigger="{ active }">
                          <div class="button is-outlined is-light has-margin-left">
                              <BIcon icon="cog"/>
                          </div>
                        </template>
                        <template v-for="column in showColumns">
                          <BDropdownItem aria-role="listitem" class="is-flex is-align-items-center" @click.native.prevent="handleColumnToggle(column, $event)">
                              <span class="is-flex is-align-items-center checkbox">
                                 <BIcon size="is-medium" :icon="selectedColumns.includes(column.field) ? 'check-square' : 'square'" style="position: relative; top: 1px;"/>
                                 {{ column.label }}
                              </span>
                          </BDropdownItem>
                        </template>
                      </BDropdown>
                    </div>

                </div>

            </div>

        </div>

        <template v-if="!loading">

            <template v-if="isMobile">
                <div class="mobile-card" v-for="row in sortedRows">
                    <template v-for="column in columns" v-if="selectedColumns.includes(column.field)">
                        <LabelLineValue :label="column.label" :value="rowValue(column, row)" :click-to-copy="column.mobileClickToCopy" />
                    </template>
                    <div class="mobile-actions">
                        <div class="mobile-action" v-for="action in row.actions" @click="action.click(row)">
                            <BIcon :icon="action.icon" />
                            <div class="mobile-action-label">{{action.label}}</div>
                        </div>
                    </div>
                </div>
            </template>

            <table v-else class="table"
                   :class="{
                   'is-fullwidth': fullWidth
                   }"
            >
                <thead>
                <tr>
                    <th v-if="bulkActions.length">
                        <input type="checkbox" v-model="selectAll" @click="handleSelectAll">
                    </th>
                    <template v-for="column in columns">
                        <th v-if="selectedColumns.includes(column.field)" :class="{ sortable: column.sortable }">
                            <div class="is-flex is-justify-content-space-between" @click="handleChangeSort(column)">

                                <span>{{ column.label }}</span>

                                <template v-if="column.sortable && sortBy === column.field">
                                    <span>
                                      <BIcon icon="arrow-down" v-if="sortOrder === 'asc'"/>
                                      <BIcon icon="arrow-up" v-else/>
                                    </span>
                                </template>
                            </div>
                        </th>
                    </template>
                    <th style="width: 50px;">
                        <BDropdown :close-on-click="false" aria-role="list" position="is-bottom-left" class="has-text-left pointer" v-if="this.showColumns" @click.native.stop>
                            <template #trigger="{ active }">
                                <BIcon icon="cog"/>
                            </template>
                            <template v-for="column in showColumns">
                                <BDropdownItem aria-role="listitem" class="is-flex is-align-items-center" @click.native.prevent="handleColumnToggle(column, $event)">
                                    <span class="is-flex is-align-items-center checkbox">
                                        <BIcon size="is-medium" :icon="selectedColumns.includes(column.field) ? 'check-square' : 'square'" style="position: relative; top: 1px;"/>
                                        {{ column.label }}
                                    </span>
                                </BDropdownItem>
                            </template>
                        </BDropdown>
                    </th>
                </tr>
                </thead>
                <tbody>
                <template v-for="row in sortedRows" v-if="sortedRows.length">
                    <tr :key="`${rowKey}-${row.id}`" @contextmenu="handleRowRightClick($event, row)">
                        <td v-if="bulkActions.length" style="width: 46px;">
                            <input type="checkbox" v-model="selected" :value="row.id">
                        </td>
                        <template v-for="column in columns">

                            <td :data-label="column.label" v-if="selectedColumns.includes(column.field)"
                                :class="{
                                  editing: editing === `${column.field}.${row.id}`,
                                  editable: column.editable,
                                }"
                                :style="{
                                  width: column.width ? `${column.width}px` : 'auto',
                                }"
                                v-on:dblclick="handleInlineEdit(column, row)"
                            >

                                <div v-if="editing === `${column.field}.${row.id}`" v-click-outside="handleFinishEditing">
                                    <TextInput v-model="form.fields[column.field]"
                                               ref="editing"
                                               @click="handleCancel"
                                               @keyup.esc.native="handleCancel"
                                               @keyup.enter.native="handleSave(row, column.field)"
                                    />
                                    <div class="control control-cancel" @click="handleCancel">
                                        <BIcon icon="times"/>
                                    </div>
                                    <div class="control control-save" @click="handleSave(row, column.field)">
                                        <BIcon icon="check"/>
                                    </div>
                                </div>

                                <div v-else>

                                    <template v-if="column.component">
                                        <component :is="column.component" :row="row" />
                                    </template>

                                    <div v-else-if="column.type === 'color'">
                                        <div
                                                class="tag"
                                                :class="{
                                                    'has-text-light': !isLight(rowValue(column, row)),
                                                    'has-text-dark': isLight(rowValue(column, row)),
                                                }"
                                                :style="{
                                                    'background-color': rowValue(column, row),
                                                }"
                                        >
                                            {{ rowValue(column, row) }}
                                        </div>
                                    </div>

                                    <div  v-else>
                                        {{ rowValue(column, row) }}
                                    </div>

                                    <span class="pointer has-margin-left" @click="handleCopy(rowValue(column, row))" v-if="column.clickToCopy">
                                        <BTooltip label="Copy" position="is-left">
                                          <BIcon icon="copy"/>
                                        </BTooltip>
                                    </span>

                                </div>

                            </td>
                        </template>
                        <td class="has-text-right">
                            <BDropdown :ref="`contextRow${row.id}`" aria-role="list" position="is-bottom-left"
                                       class="has-text-left pointer" v-if="row.actions">

                                <template #trigger="{ active }">
                                    <BIcon icon="ellipsis-h-alt"/>
                                </template>

                                <template v-for="action in row.actions">
                                    <BDropdownItem aria-role="listitem" @click="action.click(row)">
                                        <BIcon :icon="action.icon"/>
                                        {{ action.label }}
                                    </BDropdownItem>
                                </template>

                            </BDropdown>
                        </td>
                    </tr>
                    <tr v-if="isMobile" class="card-separator">
                      <td></td>
                    </tr>

                </template>
                <tr class="has-text-centered" v-if="! sortedRows.length">
                    <td :colspan="columns.length + 1">No data.</td>
                </tr>
                </tbody>
            </table>

            <template v-if="paginator">

                <div class="columns table-footer">

                    <div class="column has-text-centered-mobile">
                        <div class="paginator">
                            {{ paginator.total }} {{ pluralize('Result', paginator.total) }} <span class="separator">|</span> <br>
                            Page {{ paginator.currentPage }} of {{ paginator.totalPage }}
                        </div>
                    </div>

                    <div class="column">
                        <div class="columns is-mobile">

                            <div class="column is-two-thirds-mobile">
                                <div class="per-page is-flex is-align-items-center" style="margin-left: 10px;">
                                    Per Page:
                                    <div class="select has-margin-left">
                                        <select v-model="perPage" @change="handleChangePerPage" class="is-dark">
                                            <option>10</option>
                                            <option>25</option>
                                            <option>50</option>
                                            <option>100</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div class="column has-text-right">

                                <Button light outlined :disabled="paginator.currentPage === 1" @click="handlePrevPage">
                                    <BIcon icon="long-arrow-left"/>
                                </Button>

                                <Button light outlined style="margin-left: 10px;" :disabled="paginator.currentPage === paginator.totalPage" @click="handleNextPage">
                                    <BIcon icon="long-arrow-right"/>
                                </Button>

                            </div>

                        </div>
                    </div>

                </div>

            </template>

        </template>

      <BDropdown v-if="contextMenu.row" ref="contextRowPointer" aria-role="list"
                 class="has-text-left pointer"
                 :style="{
                            position:'absolute',
                            left: `${contextMenu.x}px`,
                            top: `${contextMenu.y}px`
                         }">
        <template v-for="action in contextMenu.row.actions">
          <BDropdownItem aria-role="listitem" @click="action.click(contextMenu.row)">
            <BIcon :icon="action.icon"/>
            {{ action.label }}
          </BDropdownItem>
        </template>
      </BDropdown>

    </div>
</template>

<script>
import {
  Form,
} from '@lakestreetsoftware/toolbox';
import { camelCase } from 'lodash';

import LabelLineValue from '../Content/LabelLineValue';
import copy from 'copy-to-clipboard';
import pluralize from 'pluralize';
import tinycolor from 'tinycolor2';
import Storage from '../Storage';
import Vue from 'vue';

export default {
  name: 'Table',
  components: {
    LabelLineValue,
  },
  computed: {
    fullPath () {
      return this.$route.fullPath;
    },
    keymap () {
      return {
        esc: this.handleCancel,
      };
    },
    key () {
      let path = this.$route.name;
      path = path.replace('/', '_');

      return camelCase(path) + 'Columns';
    },
    sortedRows () {
      return this.rows.sort((a, b) => {
        if (this.sortOrder === 'asc') {
          return a[this.sortBy] > b[this.sortBy] ? 1 : -1;
        } else {
          return a[this.sortBy] < b[this.sortBy] ? 1 : -1;
        }
      });
    },
    showColumns () {
      this.selectedColumns = Storage.getItem(this.key) || this.columns.filter(function (column) {
        return column.enabled;
      }).map((column) => {
        return column.field;
      });

      return this.columns;
    },
    showManageDropdown () {
      return this.selected.length || this.bulkActions.some((action) => {
        return action.alwaysShow;
      });
    },
    isMobile() {
      if(this.tableWidth) {
        return this.tableWidth < this.mobileWidth;
      }

      return false;
    }
  },
  created () {

    if (this.action) {
      this.loadData();
    }

    this.$nextTick(() => {
      this.tableWidth = this.$refs.lssTable.clientWidth;

      const rb = new ResizeObserver((entries) => {
        let el = entries[0].contentRect;

        this.tableWidth = el.width;
      });

      rb.observe(this.$refs.lssTable);

    });

  },
  data () {
    return {
      contextMenu: {
        active: false,
        row: null,
        x: 0,
        y: 0
      },
      editing: null,
      form: new Form({}),
      loading: false,
      paginator: null,
      page: this.$route.query.page || 1,
      perPage: this.$route.query.perPage || window.app.storage.getItem('perPage') || 10,
      refocusSearch: false,
      search: this.$route.query.search || '',
      sortBy: this.$route.query.sortBy || this.defaultSortBy || 'id',
      sortOrder: this.$route.query.sortOrder || this.defaultSortOrder || 'asc',
      selectAll: false,
      selected: [],
      selectedColumns: [],
      tableWidth: null
    };
  },
  methods: {
    handleCancelSearch () {
      this.selected = [];

      this.search = '';

      const route = Object.assign({}, this.$route);

      this.$router.push({
        name: route.name,
        query: {
          ...route.query,
          search: '',
        },
      }).then(() => {
        this.refocusSearch = true;
      });
    },
    handleChangePerPage () {
      this.selected = [];

      window.app.storage.setItem('perPage', this.perPage);

      const route = Object.assign({}, this.$route);

      this.page = 1;

      this.$router.push({
        name: route.name,
        query: {
          ...route.query,
          page: this.page,
          perPage: this.perPage,
        },
      });
    },
    handleChangeSort (column) {
      this.selected = [];

      if (!column.sortable) {
        return false;
      }

      const route = Object.assign({}, this.$route);

      if (this.sortBy !== column.field) {

        this.sortBy = column.field;
        this.sortOrder = 'asc';

        this.$router.push({
          name: route.name,
          query: {
            ...route.query,
            sortBy: column.field,
            sortOrder: 'asc',
          },
        });

      } else {

        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';

        this.$router.push({
          name: route.name,
          query: {
            ...route.query,
            sortOrder: this.sortOrder === 'asc' ? 'desc' : 'asc',
          },
        });

      }
    },
    handleColumnToggle (column, event) {
      const checked = this.selectedColumns.includes(column.field);

      let newSelectedColumns = [];

      if (checked) {
        newSelectedColumns = [
          ...this.selectedColumns.filter(field => field !== column.field),
        ];
      } else {
        newSelectedColumns = [
          ...this.selectedColumns,
          column.field,
        ];
      }

      if (newSelectedColumns.length === 0) {

        window.app.snackbar({
          type: 'is-danger',
          message: 'You must have at least one column selected',
        });

        return;
      }

      this.selectedColumns = [
        ...newSelectedColumns,
      ];

      Storage.setItem(this.key, newSelectedColumns);
    },
    handleCopy (value) {
      copy(value);
      window.app.snackbar('Copied to Clipboard');
    },
    handleCancel () {
      this.form = new Form({});
      this.editing = null;
    },
    handleBulkAction (action) {
      action.click(this.selected);
    },
    handleInlineEdit (column, row) {

      if (!column.editable || this.editing) {
        return false;
      }

      this.form = new Form({
        [column.field]: row[column.field],
      });

      this.editing = `${column.field}.${row.id}`;

      setTimeout(() => {
        try {
          this.$refs.editing[0].$el.select();
        } catch (e) {
        }
      }, 250);

    },
    handleFinishEditing () {
      this.editing = null;
    },
    handlePrevPage () {
      this.selected = [];

      if (this.paginator && this.paginator.currentPage > 1) {

        const newPage = this.paginator.currentPage - 1;

        this.page = newPage;

        const route = Object.assign({}, this.$route);

        this.$router.push({
          name: route.name,
          query: {
            ...route.query,
            page: newPage,
          },
        });
      }
    },
    handleNextPage () {
      this.selected = [];

      if (this.paginator && this.paginator.currentPage < this.paginator.totalPage) {

        const newPage = this.paginator.currentPage + 1;

        this.page = newPage;

        const route = Object.assign({}, this.$route);

        this.$router.push({
          name: route.name,
          query: {
            ...route.query,
            page: newPage,
          },
        });
      }
    },
    handleRowRightClick (event, row) {
      event.preventDefault();

      if (!row.actions) {
        return;
      }

      const rowContextMenu = this.$refs['contextRow' + row.id][0];

      // If right click on dots icon
      if(event.target.classList.contains('fa-ellipsis-h-alt')) {
        rowContextMenu.toggle();
        return;
      }

      this.contextMenu = {
        row: row,
        x: event.layerX,
        y: event.layerY
      };

      Vue.nextTick(() => {

        if(rowContextMenu && rowContextMenu.$el.classList.contains('is-active')) {
            rowContextMenu.toggle();
        }

        this.$refs['contextRowPointer'].toggle();
      });
    },
    handleSave (row, field) {

      this.$emit('update', {
        ...row,
        [field]: this.form.fields[field],
      });

      this.editing = false;
    },
    handleSearch () {

      const route = Object.assign({}, this.$route);

      this.page = 1;

      this.$router.push({
        name: route.name,
        query: {
          ...route.query,
          page: this.page,
          search: this.search,
        },
      }).then(() => {
        this.refocusSearch = true;
      });

    },
    handleSelectAll (e) {

      let checked = e.target.checked;

      if (checked) {
        this.selected = [
          ...this.rows.map(row => row.id),
        ];
      } else {
        this.selected = [];
      }

    },
    isLight (color) {
      return tinycolor(color).isLight();
    },
    loadData () {

      this.loading = true;

      const params = {
        ...this.params,
        page: this.page,
        perPage: this.perPage,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      };

      if (this.search) {
        params.search = this.search;
      }

      return this.action({
        params,
      }).then((response) => {

        if (response.paginator) {
          this.paginator = response.paginator;
        }

        this.$emit('response', response);

      }).finally(() => {
        this.loading = false;

        if (this.refocusSearch) {
          setTimeout(() => {
            this.$refs.search.$el.focus();
            this.refocusSearch = false;
          }, 250);
        }

      });
    },
    pluralize (word, value) {
      return pluralize(word, value);
    },
    rowValue (column, row) {

      let variables = column.field.split('.');
      let depth = 0;
      let value = null;

      while (depth < variables.length) {
        value = value ? value[variables[depth]] : row[variables[depth]];
        depth++;
      }

      if (column.formatter) {
        return column.formatter(row);
      }

      return value;
    }
  },
  props: {
    action: {
      required: false,
    },
    addLabel: {
      required: false,
      type: String,
    },
    addRoute: {
      required: false,
      type: String,
    },
    addRouteQuery: {
      required: false,
      type: Object,
    },
    bulkActions: {
      default: () => [],
      required: false,
      type: Array,
    },
    columns: {
      required: true,
      type: Array,
    },
    defaultSortBy: {
      type: String,
    },
    defaultSortOrder: {
      type: String,
    },
    fullWidth: {
      default: false,
      type: Boolean,
    },
    mobileWidth: {
      default: 600,
      type: Number,
    },
    params: {
      default: () => {
      },
      required: false,
    },
    rowKey: {
      default: '',
      required: false,
    },
    rows: {
      default: () => [],
      required: true,
      type: Array,
    },
    title: {
      required: false,
      type: String,
    },
    titleIcon: {
      required: false,
      type: String,
    },
  },
  watch: {
    fullPath () {
      this.loadData();
    },
    rows () {
      if (this.rows.length === 0) {
        this.selected = [];
      } else if (this.selectAll && this.rows.length !== this.selected.length) {
        this.selected = [
          ...this.rows.map(row => row.id),
        ];
      }
    },
    selected () {
      this.selectAll = this.selected.length === this.rows.length;
    },
  },
};
</script>

<style lang="scss">

.lss-table {

    .table-controls {

        display: flex;
        justify-content: flex-end;

        .search {
            position: relative;

            .search-icon {
                position: absolute;
                top: 10px;
                left: 7.5px;
                z-index: 2;
            }

            input.input[type="text"] {
                padding-left: 35px;
            }

            .search-cancel {
                position: absolute;
                top: 10px;
                right: 7.5px;
                z-index: 2;
            }
        }
    }

    .table-footer {
      .paginator {
        br {
          display:none;
        }
      }
    }

    .table {
        background: transparent;
        border: 1px solid $grey-dark;

        tr {

            th {
                background: $black-bis;
                border-color: $grey-dark;
                color: $white;

                &.sortable:hover {
                    background: $grey-darker;
                    cursor: pointer;
                }
            }

            td {
                border-color: $grey-dark;
                color: $white;
                cursor: default;

                &.editable {
                    &:hover {
                        background: $grey-darker;
                    }
                }

                &.editing {
                    padding: 0;

                    & > div {
                        align-items: center;
                        background: $grey-darker;
                        display: inline-flex;
                        width: 100%;

                        .input {
                            background: transparent;
                            border: none;
                            border-radius: 0;
                            flex: 1;
                            margin: 0;
                        }

                        .control {
                            cursor: pointer;
                            padding: 5px;
                        }
                    }
                }
            }
        }

    }

    .mobile-card {
        background: $black-ter;
        border: 1px solid $grey-dark;
        border-radius: 4px;
        margin: 15px 0;
        padding: 15px;

        .label-line-value:first-child {
            margin-top: 0;
        }

        .mobile-actions {
            border-top: 1px solid $grey-darker;
            display: flex;
            margin: 15px 0 0;
            padding-top: 15px;

            .mobile-action {
                align-items: center;
                background: $black-bis;
                border-radius: 4px;
                display: flex;
                flex: 1;
                flex-direction: column;
                justify-content: center;
                margin: 0 5px;
                padding: 5px 0;

                &:first-child {
                    margin-left: 0;
                }

                &:last-child {
                    margin-right: 0;
                }

                .icon {

                }

                .mobile-action-label {
                    font-size: 0.7em;
                    text-transform: uppercase;
                }
            }
        }
    }
}
</style>
